import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowUpRight, Receipt, ShoppingBag, TrendingUp, Wallet } from 'lucide-react';
import { relatoriosApi } from '../relatorios/api';
import { Skeleton } from '../../shared/components/Skeleton';
import { formatBRL } from '../../shared/lib/format';
import { useAuthStore } from '../../store/authStore';
import { useCaixaAtual } from '../caixa/hooks';
import { useComandas } from '../comandas/hooks';

function startOfTodayISO(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function startOfLast7DaysISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const { data: caixa } = useCaixaAtual();
  const { data: comandasAbertas } = useComandas({ status: 'aberta' });

  const hojeIso = useMemo(() => startOfTodayISO(), []);
  const ultimos7Iso = useMemo(() => startOfLast7DaysISO(), []);

  const kpisQuery = useQuery({
    queryKey: ['relatorios', 'kpis-hoje'],
    queryFn: () => relatoriosApi.kpis({ inicio: hojeIso }),
    refetchInterval: 30_000,
  });

  const porHoraQuery = useQuery({
    queryKey: ['relatorios', 'vendas-por-hora-hoje'],
    queryFn: () => relatoriosApi.vendasPorHora({ inicio: hojeIso }),
    refetchInterval: 30_000,
  });

  const semanaQuery = useQuery({
    queryKey: ['relatorios', 'vendas-por-dia-7d'],
    queryFn: () => relatoriosApi.vendasPorDia({ inicio: ultimos7Iso }),
    refetchInterval: 60_000,
  });

  const categoriasQuery = useQuery({
    queryKey: ['relatorios', 'categorias-hoje'],
    queryFn: () => relatoriosApi.vendasPorCategoria({ inicio: hojeIso }),
    refetchInterval: 60_000,
  });

  const topQuery = useQuery({
    queryKey: ['relatorios', 'top-produtos-hoje'],
    queryFn: () => relatoriosApi.topProdutos({ inicio: hojeIso, limit: 5 }),
    refetchInterval: 60_000,
  });

  const kpis = kpisQuery.data;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">
          Bem-vindo, {user?.nome}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Acompanhe o movimento da loja em tempo real.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Faturamento hoje"
          value={kpis ? formatBRL(Number(kpis.receita)) : undefined}
          icon={TrendingUp}
          accent="brand"
          loading={kpisQuery.isLoading}
        />
        <KpiCard
          label="Vendas hoje"
          value={kpis ? String(kpis.vendas) : undefined}
          icon={Receipt}
          loading={kpisQuery.isLoading}
        />
        <KpiCard
          label="Ticket medio"
          value={kpis ? formatBRL(Number(kpis.ticket_medio)) : undefined}
          icon={ShoppingBag}
          loading={kpisQuery.isLoading}
        />
        <KpiCard
          label="Comandas abertas"
          value={String((comandasAbertas ?? []).length)}
          icon={Wallet}
          accent={caixa ? 'emerald' : 'slate'}
          href="/comandas"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">
              Vendas por dia (ultimos 7 dias)
            </h3>
            <Link
              to="/relatorios"
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
            >
              Ver relatorios <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="h-64">
            {semanaQuery.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (semanaQuery.data?.rows ?? []).length === 0 ? (
              <p className="flex h-full items-center justify-center text-sm text-slate-400">
                Sem vendas no periodo
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={(semanaQuery.data?.rows ?? []).map((r) => ({
                    dia: new Date(r.dia).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                    }),
                    total: Number(r.total),
                    vendas: Number(r.qtd_vendas),
                  }))}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="dia" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickFormatter={(v: number) => `R$${v.toFixed(0)}`}
                  />
                  <Tooltip
                    formatter={(value: unknown, name: unknown) =>
                      name === 'total'
                        ? [formatBRL(Number(value)), 'Receita']
                        : [String(value), 'Vendas']
                    }
                  />
                  <Bar dataKey="total" fill="#6d28d9" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">
            Categorias vendidas hoje
          </h3>
          <div className="h-64">
            {categoriasQuery.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (categoriasQuery.data?.rows ?? []).length === 0 ? (
              <p className="flex h-full items-center justify-center text-sm text-slate-400">
                Sem vendas hoje
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoriasQuery.data!.rows}
                    dataKey="receita"
                    nameKey="categoria_nome"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {categoriasQuery.data!.rows.map((entry, i) => (
                      <Cell key={entry.categoria_id} fill={entry.cor || `hsl(${i * 53}, 70%, 50%)`} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: unknown) => formatBRL(Number(v))} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">
            Movimento por hora (hoje)
          </h3>
          <div className="h-56">
            {porHoraQuery.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (porHoraQuery.data?.rows ?? []).length === 0 ? (
              <p className="flex h-full items-center justify-center text-sm text-slate-400">
                Nenhuma venda registrada hoje
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={(porHoraQuery.data?.rows ?? []).map((r) => ({
                    hora: new Date(r.hora).getHours() + 'h',
                    total: Number(r.total),
                    vendas: Number(r.qtd_vendas),
                  }))}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="hora" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip formatter={(v: unknown) => [formatBRL(Number(v)), 'Receita']} />
                  <Bar dataKey="total" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">
            Top 5 produtos hoje
          </h3>
          {topQuery.isLoading ? (
            <Skeleton className="h-56" />
          ) : (topQuery.data?.rows ?? []).length === 0 ? (
            <p className="flex h-56 items-center justify-center text-sm text-slate-400">
              Sem produtos vendidos ainda
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {topQuery.data!.rows.map((p, i) => (
                <li
                  key={p.produto_id}
                  className="flex items-center justify-between gap-3 py-2 text-sm"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                      {i + 1}
                    </span>
                    <span className="truncate font-medium text-slate-900">
                      {p.produto_nome}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-brand-700">
                      {formatBRL(Number(p.receita_total))}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {Number(p.quantidade_total).toLocaleString('pt-BR')} vendidos
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  accent = 'slate',
  loading,
  href,
}: {
  label: string;
  value?: string;
  icon: React.ComponentType<{ className?: string }>;
  accent?: 'brand' | 'emerald' | 'slate';
  loading?: boolean;
  href?: string;
}) {
  const colors: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    slate: 'bg-slate-100 text-slate-600',
  };
  const content = (
    <div className="card flex items-center gap-4 p-5">
      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${colors[accent]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {label}
        </div>
        <div className="mt-0.5 text-xl font-semibold text-slate-900">
          {loading || !value ? <Skeleton className="mt-1 h-5 w-20" /> : value}
        </div>
      </div>
    </div>
  );

  return href ? (
    <Link to={href} className="block transition hover:-translate-y-0.5">
      {content}
    </Link>
  ) : (
    content
  );
}


